import { DownOutlined, EditOutlined, UpOutlined } from '@ant-design/icons';
import { RegPlate, SharedCard, VehicleForm } from '@components';
import { L } from '@lib';
import { RefreshVehicleData, UpdateVehicle, Vehicle } from '@services';
import { useStore } from '@stores';
import { Button, Col, Form, Input, Modal, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { VehicleDescriptions } from './VehicleDescriptions';

interface IProps {
  vehicle: Vehicle;
  showRefresh?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  bordered?: boolean;
  hideHeader?: boolean;
  setSelectedVehicle?: (vehicle: Vehicle | null) => void;
}

export const VehicleDetailsCard: React.FC<IProps> = observer(
  ({ vehicle, showRefresh, collapsible = false, defaultExpanded = true, bordered = true, hideHeader = false, setSelectedVehicle }) => {
    const { vehicleStore } = useStore();
    const [form] = Form.useForm();
    const [refreshForm] = Form.useForm();
    const [showEdit, setShowEdit] = React.useState(false);
    const [expanded, setExpanded] = useState(defaultExpanded);

    useEffect(() => {
      form.setFieldsValue({
        ...vehicle,
        dateFirstRegistered: vehicle.dateFirstRegistered
          ? dayjs(vehicle.dateFirstRegistered)
          : undefined,
        startDateOfCurrentKeeper: vehicle.startDateOfCurrentKeeper
          ? dayjs(vehicle.startDateOfCurrentKeeper)
          : undefined,
        motExpiryDate: vehicle.motExpiryDate ? dayjs(vehicle.motExpiryDate) : undefined,
        exemptFromMot: vehicle.dateFirstRegistered
          ? dayjs().diff(vehicle.dateFirstRegistered, 'years') >= 40
          : false,
      });
    }, [form, vehicle]);

    const onFinish = useCallback(
      async (values: UpdateVehicle) => {
        await vehicleStore.update(values).then((result) => {
          setShowEdit(false);
          if (!result) return;
          setSelectedVehicle?.(result);
        });
      },
      [vehicleStore]
    );

    const onRefreshVehicleData = useCallback(
      async (values: RefreshVehicleData) => {
        await vehicleStore.refreshVehicleData(values).then((result) => {
          if (!result) {
            setShowEdit(true);
          } else {
            setSelectedVehicle?.(result);
            refreshForm.resetFields();
          }
        });
      },
      [vehicleStore, setSelectedVehicle]
    );

    const cardContent = (
      <>
        {!hideHeader && (
          <Row
            justify="space-between"
            align="middle"
            style={{ marginTop: 8, marginBottom: collapsible && !expanded ? 8 : 12, cursor: collapsible ? 'pointer' : undefined }}
            onClick={collapsible ? () => setExpanded((prev) => !prev) : undefined}
          >
            <Col>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RegPlate>{vehicle.registrationNumber}</RegPlate>
                {vehicle.manuallyAdded && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEdit(true);
                    }}
                  />
                )}
              </div>
            </Col>
            {collapsible && (
              <Col>
                {expanded ? <UpOutlined /> : <DownOutlined />}
              </Col>
            )}
          </Row>
        )}
        {(!collapsible || expanded) && (
          <>
            {showRefresh && (
              <Form
                layout="inline"
                id="vehicle-vin-form"
                form={refreshForm}
                onFinish={onRefreshVehicleData}
                initialValues={{ registrationNumber: vehicle.registrationNumber }}
                style={{ marginBottom: 12 }}
              >
                <Form.Item>
                  <Typography.Text strong>Not your vehicle?</Typography.Text>
                </Form.Item>
                <Form.Item hidden name="registrationNumber">
                  <Input />
                </Form.Item>
                <Form.Item
                  name="vin"
                  required
                  rules={[{ required: true, message: 'Please enter a VIN' }]}
                >
                  <Input placeholder="Enter vin" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    form="vehicle-vin-form"
                    loading={vehicleStore.refreshLoading}
                    disabled={vehicleStore.refreshLoading}
                  >
                    Refresh
                  </Button>
                </Form.Item>
              </Form>
            )}
            <VehicleDescriptions vehicle={vehicle} />
          </>
        )}
      </>
    );

    return (
      <>
        {bordered ? <SharedCard size="small">{cardContent}</SharedCard> : cardContent}
        <Modal
          open={showEdit}
          onCancel={() => setShowEdit(false)}
          footer={null}
          width={1200}
          destroyOnHidden
          title={L('Edit Vehicle')}
        >
          <VehicleForm form={form} onFinish={onFinish} onCancel={() => setShowEdit(false)} />
        </Modal>
      </>
    );
  }
);
